import fetchClient, { ApiResponse } from '@/api/api-client'

import type { Address, CreateAddressPayload, UpdateAddressPayload } from './types/Address'
import type { PlaceCity, PlaceStreet } from './types/Place'

export async function getAddresses(userId?: string): Promise<Address[]> {
  try {
    // If userId is provided, append as query param (requires backend support)
    // For now, this will only work for the authenticated user
    const url = userId ? `/addresses?userId=${encodeURIComponent(userId)}` : '/addresses'
    const res = await fetchClient.get<ApiResponse<Address[]>>(url)
    return res.data.data || []
  } catch (err) {
    // If fetching for specific user fails, return empty array (may not be supported)
    if (userId) {
      return []
    }
    throw err
  }
}

export async function createAddress(address: CreateAddressPayload): Promise<Address> {
  try {
    const res = await fetchClient.post<ApiResponse<Address>>('/addresses', address)
    return res.data.data
  } catch (err) {
    throw err
  }
}

export async function updateAddress(addressId: string, address: UpdateAddressPayload): Promise<Address> {
  try {
    const res = await fetchClient.patch<ApiResponse<Address>>(`/addresses/${addressId}`, address)
    return res.data.data
  } catch (err) {
    throw err
  }
}

export async function deleteAddress(addressId: string): Promise<void> {
  try {
    await fetchClient.delete(`/addresses/${addressId}`)
  } catch (err) {
    throw err
  }
}

export async function setDefaultAddress(addressId: string): Promise<Address> {
  try {
    const res = await fetchClient.post<ApiResponse<Address>>(`/addresses/${addressId}/set-default`)
    return res.data.data
  } catch (err) {
    throw err
  }
}

export async function getCities(query?: string): Promise<PlaceCity[]> {
  const url = query?.trim() ? `/addresses/places/cities?q=${encodeURIComponent(query.trim())}` : '/addresses/places/cities'
  const res = await fetchClient.get<ApiResponse<{ cities: PlaceCity[] }>>(url)
  return res.data.data?.cities ?? []
}

export async function getStreetsByCityId(cityId: number, _query?: string): Promise<PlaceStreet[]> {
  const res = await fetchClient.get<ApiResponse<{ streets: PlaceStreet[] }>>(
    `/addresses/places/streets?cityId=${encodeURIComponent(String(cityId))}`
  )
  return res.data.data?.streets ?? []
}
